import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
	Squares2X2Icon,
	PlusCircleIcon,
	ChartBarIcon,
	Cog6ToothIcon,
	ClipboardDocumentListIcon,
	WrenchScrewdriverIcon,
	UsersIcon,
	ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';

import { cn } from '../ui';
import { useAuth } from '../auth/AuthContext';
import { isInsurance, isMechanic, isSupplier, RoleKey } from '../auth/roles';
import { useSubmittedCarReportsCount } from '../api/hooks';

type Translator = (key: string) => string;

interface NavItem {
	to: string;
	label: string;
	icon: React.ReactNode;
	exact?: boolean;
	roles?: RoleKey[];
	/**
	 * Optional badge marker. The sidebar resolves the actual count via
	 * the matching query hook, so navItemsFor stays pure and trivially
	 * testable. Today: `submittedReports` powers the "new mechanic
	 * reports waiting for action" badge next to /car-reports.
	 */
	badgeKind?: 'submittedReports';
}

/**
 * Build the nav for a given role, picking labels from the `navigation`
 * i18n namespace. Translator is passed in (not called inside) so the
 * function stays pure and trivially testable.
 */
function navItemsFor(role: RoleKey | null | undefined, t: Translator): NavItem[] {
	const items: NavItem[] = [];

	if (isInsurance(role) || role === 'SUPER_ADMIN') {
		items.push(
			{
				to: '/requests',
				label: t('insurance.allRequests'),
				icon: <Squares2X2Icon className="h-5 w-5" />,
				exact: true, // don't light up when on /requests/new or /requests/:id
			},
			{
				to: '/requests/new',
				label: t('insurance.createRequest'),
				icon: <PlusCircleIcon className="h-5 w-5" />,
				exact: true,
			},
			{
				to: '/car-reports',
				label: t('insurance.mechanicReports'),
				icon: <ClipboardDocumentListIcon className="h-5 w-5" />,
				badgeKind: 'submittedReports',
			},
			{
				to: '/analytics',
				label: t('insurance.analytics'),
				icon: <ChartBarIcon className="h-5 w-5" />,
			}
		);
	}

	if (isSupplier(role)) {
		items.push(
			{
				to: '/supplier',
				label: t('supplier.openRequests'),
				icon: <Squares2X2Icon className="h-5 w-5" />,
				exact: true,
			},
			{
				to: '/supplier/quotations',
				label: t('supplier.myQuotations'),
				icon: <ClipboardDocumentListIcon className="h-5 w-5" />,
			}
		);
	}

	if (isMechanic(role)) {
		items.push(
			{
				to: '/mechanic',
				label: t('mechanic.myReports'),
				icon: <ClipboardDocumentListIcon className="h-5 w-5" />,
				exact: true,
			},
			{
				to: '/mechanic/new',
				label: t('mechanic.newReport'),
				icon: <WrenchScrewdriverIcon className="h-5 w-5" />,
				exact: true,
			},
			// Mechanic-direct flow surface — Requests they created themselves
			// (kind='direct'), not the CarReports filed for an insurance.
			// Reuses the insurance /requests list + /requests/:id detail; the
			// service-layer tenant scoping returns only the mechanic's own
			// Requests, so the page renders correctly without a dedicated
			// mechanic variant.
			{
				to: '/requests',
				label: t('mechanic.myRequests'),
				icon: <Squares2X2Icon className="h-5 w-5" />,
				exact: true,
			}
		);
	}

	if (
		role === 'SUPER_ADMIN' ||
		role === 'INSURANCE_ADMIN' ||
		role === 'SUPPLIER_ADMIN'
	) {
		items.push({
			to: '/team',
			label: t('common.team'),
			icon: <UsersIcon className="h-5 w-5" />,
		});
	}

	items.push({
		to: '/settings',
		label: t('common.settings'),
		icon: <Cog6ToothIcon className="h-5 w-5" />,
	});

	return items;
}

export interface SidebarContentProps {
	onNavigate?: () => void; // optional, used by mobile drawer to auto-close
}

export function SidebarContent({ onNavigate }: SidebarContentProps) {
	const { user, logout } = useAuth();
	const { t } = useTranslation();
	// All sidebar strings live in the `navigation` namespace; we bind the
	// prefix once so navItemsFor (pure function) can stay namespace-agnostic.
	const tn: Translator = (key) => t(`navigation:${key}`);
	const items = navItemsFor(user?.role, tn);
	// Badge counts. The hook returns 0 for non-insurance callers so we can
	// call it unconditionally without leaking insurance-specific UI to
	// mechanics/suppliers — the badge just renders nothing.
	const submittedReportsCount = useSubmittedCarReportsCount().data?.count ?? 0;
	const badgeCountFor = (kind: NavItem['badgeKind']): number => {
		if (kind === 'submittedReports') return submittedReportsCount;
		return 0;
	};

	return (
		// `w-full min-w-0` is the critical pair — the parent <aside> is a
		// flex row (`lg:flex lg:w-64`), and without min-w-0 long content
		// (user.name, role label, nav i18n keys) pushes this div past
		// 256px, breaking the `truncate` everywhere inside. Don't drop it.
		<div className="h-full w-full min-w-0 flex flex-col bg-brand-800 text-white/90">
			{/* Brand — height-locked to h-14 to match the main column header
			    (MainLayout.tsx). Without this the sidebar's brand block was
			    20px taller than the header, leaving a visible void where the
			    bottom borders didn't align across the column boundary. */}
			<div className="h-14 px-5 border-b border-white/10 flex items-center gap-3 flex-shrink-0">
				<div className="h-8 w-8 rounded-lg bg-white/10 grid place-items-center flex-shrink-0">
					<svg
						viewBox="0 0 24 24"
						className="h-4 w-4"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					>
						<path
							d="M3 13h18l-2-7H5l-2 7zm0 0v6h18v-6M7 17h2m6 0h2"
							strokeLinecap="round"
						/>
					</svg>
				</div>
				<div className="min-w-0">
					<div className="text-sm font-semibold tracking-tight leading-tight">
						SPARTS <span className="font-light text-white/60">auto</span>
					</div>
					<div className="text-2xs text-white/50 leading-tight">{tn('productLine')}</div>
				</div>
			</div>

			{/* Nav */}
			<nav className="flex-1 px-3 py-4 space-y-1 scrollbar-thin overflow-y-auto">
				{items.map((it) => {
					const badge = it.badgeKind ? badgeCountFor(it.badgeKind) : 0;
					return (
						<NavLink
							key={it.to}
							to={it.to}
							end={it.exact}
							onClick={onNavigate}
							className={({ isActive }) =>
								cn(
									'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
									isActive
										? 'bg-white/10 text-white'
										: 'text-white/70 hover:bg-white/5 hover:text-white'
								)
							}
						>
							<span className="flex-shrink-0">{it.icon}</span>
							<span className="truncate min-w-0 flex-1">{it.label}</span>
							{badge > 0 && (
								// Pill-shaped count badge — coral so it reads as
								// "action needed", visible against the dark sidebar.
								// `99+` cap so a stale batch doesn't blow the layout.
								<span
									className="ms-2 flex-shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 rounded-full bg-danger-600 px-1.5 text-[11px] font-semibold text-white"
									aria-label={`${badge} new`}
								>
									{badge > 99 ? '99+' : badge}
								</span>
							)}
						</NavLink>
					);
				})}
			</nav>

			{/* User card */}
			<div className="border-t border-white/10 p-3">
				{user && (
					<div className="flex items-center gap-3 px-2 py-2 min-w-0">
						<div className="h-9 w-9 rounded-full bg-white/10 grid place-items-center text-sm font-semibold flex-shrink-0">
							{user.name?.charAt(0).toUpperCase() || '?'}
						</div>
						<div className="min-w-0 flex-1">
							<div className="text-sm font-medium text-white truncate">
								{user.name}
							</div>
							<div className="text-xs text-white/50 truncate">
								{user.role || '—'}
							</div>
						</div>
					</div>
				)}
				<button
					onClick={() => logout()}
					className="mt-2 w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-white/70 hover:bg-white/5 hover:text-white"
				>
					<ArrowRightOnRectangleIcon className="h-4 w-4" />
					{tn('common.signOut')}
				</button>
			</div>
		</div>
	);
}

export default SidebarContent;
